import { Card, CardContent } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

// Define types for footer elements
type ElementType = "logo" | "copyright" | "sitemap" | "social" | "contact";

interface SelectedElement {
    id: string;
    elementType: ElementType;
    html: string;
}

interface ElementSelectionMap {
    logo: string | null;
    copyright: string | null;
    sitemap: string | null;
    social: string | null;
    contact: string | null;
}

interface Props {
    onImport: (html: string) => void;
}

const FooterTemplateImporter: React.FC<Props> = ({ onImport }) => {
    const [step, setStep] = useState<number>(1);
    const [htmlCode, setHtmlCode] = useState<string>("");
    const [selectedElements, setSelectedElements] =
        useState<ElementSelectionMap>({
            logo: null,
            copyright: null,
            sitemap: null,
            social: null,
            contact: null,
        });
    const [parsedDOM, setParsedDOM] = useState<HTMLElement | null>(null);
    const [currentElementType, setCurrentElementType] =
        useState<ElementType | null>(null);
    const [selectableElements, setSelectableElements] = useState<HTMLElement[]>(
        []
    );
    const previewContainerRef = useRef<HTMLDivElement>(null);

    // Parse HTML and create a manipulable DOM structure
    const parseHTML = (html: string): HTMLElement | null => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            return doc.body.firstChild as HTMLElement;
        } catch (error) {
            console.error("Error parsing HTML:", error);
            return null;
        }
    };

    // Add unique IDs to all elements that don't have one
    const addUniqueIds = (
        element: HTMLElement | null,
        prefix = "elem"
    ): HTMLElement | null => {
        if (!element) return null;

        // Clone the element to avoid modifying the original
        const clonedElement = element.cloneNode(true) as HTMLElement;

        // Get all elements without IDs
        const elementsWithoutId = clonedElement.querySelectorAll("*:not([id])");

        // Add unique IDs
        elementsWithoutId.forEach((el, index) => {
            (el as HTMLElement).id = `${prefix}-${index}`;
        });

        return clonedElement;
    };

    // Find selectable elements based on element type
    const findSelectableElements = (
        rootElement: HTMLElement,
        type: ElementType
    ): HTMLElement[] => {
        if (!rootElement) return [];

        let selectors: string[] = [];

        switch (type) {
            case "logo":
                // Logo: img elements with containers
                selectors = [
                    "img",
                    "svg",
                    ".logo",
                    '[class*="logo"]',
                    '[id*="logo"]',
                    "a:has(img)",
                ];
                break;
            case "copyright":
                // Copyright: text elements, p elements with copyright symbols
                selectors = [
                    'p:contains("©")',
                    'div:contains("©")',
                    'span:contains("©")',
                    '[class*="copyright"]',
                    '[id*="copyright"]',
                    "small",
                    ".footer-bottom",
                    ".copyright",
                ];
                break;
            case "sitemap":
                // Sitemap: navigation elements, lists of links
                selectors = [
                    "nav",
                    "ul",
                    ".sitemap",
                    ".footer-nav",
                    ".footer-links",
                    ".footer-menu",
                    '[class*="sitemap"]',
                    '[class*="footer-links"]',
                    '[class*="footer-nav"]',
                    '[id*="sitemap"]',
                    '[id*="footer-links"]',
                ];
                break;
            case "social":
                // Social: social media links, icons
                selectors = [
                    ".social",
                    '[class*="social"]',
                    '[id*="social"]',
                    ".social-icons",
                    ".social-links",
                    'a[href*="facebook"]',
                    'a[href*="twitter"]',
                    'a[href*="instagram"]',
                    'a[href*="linkedin"]',
                    'div:has(> a[href*="facebook"])',
                    'ul:has(> li > a[href*="facebook"])',
                ];
                break;
            case "contact":
                // Contact: contact information, addresses, phone numbers
                selectors = [
                    ".contact",
                    '[class*="contact"]',
                    '[id*="contact"]',
                    "address",
                    '[class*="address"]',
                    'a[href^="tel:"]',
                    'a[href^="mailto:"]',
                    'div:has(> a[href^="tel:"])',
                    'div:has(> a[href^="mailto:"])',
                ];
                break;
            default:
                return [];
        }

        // Combine all selectors and get unique elements
        const elements = new Set<HTMLElement>();

        selectors.forEach((selector) => {
            try {
                const selected = rootElement.querySelectorAll(selector);
                selected.forEach((el) => elements.add(el as HTMLElement));
            } catch {
                // Skip invalid selectors
            }
        });

        return Array.from(elements);
    };

    // Handle code input submission
    const handleCodeSubmit = (): void => {
        try {
            const domElement = parseHTML(htmlCode);
            if (!domElement) throw new Error("Failed to parse HTML");

            const processedElement = addUniqueIds(domElement);
            setParsedDOM(processedElement);
            setStep(2);
        } catch (error) {
            alert("Invalid HTML. Please check your code and try again.");
            console.error("Error parsing HTML:", error);
        }
    };

    // Select an element for a specific role
    const selectElement = (elementId: string, type: ElementType): void => {
        setSelectedElements({
            ...selectedElements,
            [type]: elementId,
        });

        // Move to next element type or finish
        const elementTypes: ElementType[] = [
            "logo",
            "copyright",
            "sitemap",
            "social",
            "contact",
        ];
        const currentIndex = elementTypes.indexOf(type);

        if (currentIndex < elementTypes.length - 1) {
            setCurrentElementType(elementTypes[currentIndex + 1]);
        } else {
            // All elements selected, go to final step
            setStep(4);
        }
    };

    // handle finish and import
    const handleFinish = (): void => {
        const finalCode = generateFinalCode();

        if (finalCode) {
            onImport(finalCode);
        }
    };

    // Update selectable elements when element type changes
    useEffect(() => {
        if (parsedDOM && currentElementType) {
            const elements = findSelectableElements(
                parsedDOM,
                currentElementType
            );
            setSelectableElements(elements);
        }
    }, [parsedDOM, currentElementType]);

    // Apply highlighting to elements in the preview container
    useEffect(() => {
        if (!previewContainerRef.current || !selectableElements.length) return;

        // Clear existing highlights
        const allHighlighted =
            previewContainerRef.current.querySelectorAll(".element-highlight");
        allHighlighted.forEach((el) => {
            el.classList.remove(
                "element-highlight",
                "border-2",
                "border-blue-400",
                "hover:border-blue-600",
                "cursor-pointer"
            );
        });

        // Apply new highlights
        selectableElements.forEach((element) => {
            const elementId = element.id;
            const elementInPreview = previewContainerRef.current?.querySelector(
                `#${elementId || "unique"}`
            );

            if (elementInPreview) {
                elementInPreview.classList.add(
                    "element-highlight",
                    "border-2",
                    "border-blue-400",
                    "hover:border-blue-600",
                    "cursor-pointer"
                );

                // Add click handler
                elementInPreview.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (currentElementType) {
                        selectElement(elementId, currentElementType);
                    }
                });
            }
        });

        // Cleanup function to remove event listeners
        return () => {
            selectableElements.forEach((element) => {
                const elementId = element.id;
                const elementInPreview =
                    previewContainerRef.current?.querySelector(
                        `#${elementId || "unique"}`
                    );
                if (elementInPreview) {
                    elementInPreview.replaceWith(
                        elementInPreview.cloneNode(true)
                    );
                }
            });
        };
    }, [selectableElements, currentElementType]);

    // Generate the final HTML code with all selections
    const generateFinalCode = (): string => {
        if (!parsedDOM) return "";

        const finalElement = parsedDOM.cloneNode(true) as HTMLElement;

        // Add data attributes for selected elements
        Object.entries(selectedElements).forEach(([type, id]) => {
            if (id) {
                const element = finalElement.querySelector(
                    `#${id || "unique"}`
                );
                if (element) {
                    element.setAttribute("data-element-type", type);
                }
            }
        });

        return finalElement.outerHTML;
    };

    // Render preview with selectable elements
    const renderPreview = (): JSX.Element | null => {
        if (!parsedDOM) return null;

        return (
            <div
                ref={previewContainerRef}
                className="border p-4 bg-white relative"
                dangerouslySetInnerHTML={{ __html: parsedDOM.outerHTML }}
            />
        );
    };

    return (
        <div className="container mx-auto p-4 max-h-[90vh] overflow-auto">
            <Card className="mb-6">
                <CardContent className="pt-6">
                    <div className="flex items-center mb-6">
                        {[1, 2, 3, 4].map((stepNumber) => (
                            <div key={stepNumber} className="flex items-center">
                                <div
                                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                        step >= stepNumber
                                            ? "bg-blue-600 text-white"
                                            : "bg-gray-200 text-gray-600"
                                    }`}
                                >
                                    {stepNumber}
                                </div>
                                {stepNumber < 4 && (
                                    <div
                                        className={`h-1 w-16 ${
                                            step > stepNumber
                                                ? "bg-blue-600"
                                                : "bg-gray-200"
                                        }`}
                                    ></div>
                                )}
                            </div>
                        ))}
                    </div>

                    {step === 1 && (
                        <div>
                            <h2 className="text-xl font-bold mb-4">
                                Paste Your Footer Template Code
                            </h2>
                            <textarea
                                className="w-full h-64 p-4 border rounded font-mono text-sm"
                                value={htmlCode}
                                onChange={(e) => setHtmlCode(e.target.value)}
                                placeholder="Paste your HTML template code here..."
                            />
                            <button
                                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                onClick={handleCodeSubmit}
                            >
                                Next Step
                            </button>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <h2 className="text-xl font-bold mb-4">
                                Preview Your Footer Template
                            </h2>
                            <div className="mb-4 border rounded overflow-hidden">
                                {renderPreview()}
                            </div>
                            <button
                                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                onClick={() => {
                                    setStep(3);
                                    setCurrentElementType("logo");
                                }}
                            >
                                Start Element Selection
                            </button>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <h2 className="text-xl font-bold mb-4">
                                Select {currentElementType} Element
                            </h2>
                            <div className="mb-4">
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded mb-2">
                                    <h3 className="font-semibold text-blue-800">
                                        Instructions:
                                    </h3>
                                    <p className="text-blue-700">
                                        Click on the highlighted element that
                                        represents your {currentElementType}.
                                        {currentElementType === "logo" &&
                                            " Look for images or logo containers."}
                                        {currentElementType === "copyright" &&
                                            " Look for copyright text, typically with © symbol."}
                                        {currentElementType === "sitemap" &&
                                            " Look for navigation menus, site links, or list of page links."}
                                        {currentElementType === "social" &&
                                            " Look for social media icons or links."}
                                        {currentElementType === "contact" &&
                                            " Look for contact information, address, phone or email links."}
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    {selectableElements.length === 0 && (
                                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded">
                                            No suitable elements found for{" "}
                                            {currentElementType}
                                        </span>
                                    )}
                                    <button
                                        className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
                                        onClick={() => {
                                            if (currentElementType) {
                                                selectElement(
                                                    "",
                                                    currentElementType
                                                );
                                            }
                                        }}
                                    >
                                        Skip this element
                                    </button>
                                </div>
                            </div>
                            <div className="mb-4 border rounded overflow-hidden">
                                {renderPreview()}
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div>
                            <h2 className="text-xl font-bold mb-4">
                                Final Footer Template
                            </h2>
                            <div className="mb-4">
                                <h3 className="font-semibold mb-2">
                                    Selected Elements:
                                </h3>
                                <ul className="list-disc pl-6 mb-4">
                                    {Object.entries(selectedElements).map(
                                        ([type, id]) => (
                                            <li key={type}>
                                                {type}:{" "}
                                                {id
                                                    ? `#${id}`
                                                    : "None selected"}
                                            </li>
                                        )
                                    )}
                                </ul>
                            </div>
                            <div className="mb-4 border rounded overflow-hidden">
                                {renderPreview()}
                            </div>
                            <div className="mt-4">
                                <h3 className="font-semibold mb-2">
                                    Generated Code:
                                </h3>
                                <pre className="p-4 bg-gray-100 rounded overflow-auto text-sm max-h-[30vh]">
                                    {generateFinalCode()}
                                </pre>
                            </div>
                            <button
                                className="mt-4 px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                onClick={handleFinish}
                            >
                                Finish & Import
                            </button>
                            <button
                                className="mt-4 ml-2 px-6 py-2 border border-gray-300 rounded hover:bg-gray-100"
                                onClick={() => setStep(1)}
                            >
                                Start Over
                            </button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default FooterTemplateImporter;
